import React, { useEffect, useState } from 'react';
import { MapPin, Phone, Mail, Clock, Linkedin, Facebook, Instagram } from 'lucide-react';
import { Alert, AlertDescription } from './Alert';

const ContactUs = () => {
  const [isFormLoaded, setIsFormLoaded] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!document.querySelector('#hubspotForm > iframe')) {
      const script = document.createElement('script');
      script.src = "//js.hsforms.net/forms/embed/v2.js";
      script.charset = "utf-8";
      script.type = "text/javascript";
      script.onload = () => {
        window.hbspt.forms.create({
          region: "na1",
          portalId: "47638375",
          formId: "0edfb5a6-1b23-4220-b4bc-7904a82b7fb8",
          target: "#hubspotForm",
          onFormSubmitted: () => {
            setFormSubmitted(true);
          }
        });
        setIsFormLoaded(true);
      };

      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }
  }, []);

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6 text-blue-600" />,
      title: "Phone",
      details: ["+1 (848) 248-5351", "+1 (800) 123-4567"],
      action: { label: "Call Us", href: "tel:+18482485351" }
    },
    {
      icon: <Mail className="w-6 h-6 text-blue-600" />,
      title: "Email",
      details: ["hello@goaiguru.com", "support@goaiguru.com"],
      action: { label: "Email Us", href: "mailto:hello@goaiguru.com" }
    },
    {
      icon: <MapPin className="w-6 h-6 text-blue-600" />,
      title: "Office",
      details: ["Princeton, NJ 08536"],
      action: { label: "Get Directions", href: "https://maps.google.com" }
    },
    {
      icon: <Clock className="w-6 h-6 text-blue-600" />,
      title: "Hours",
      details: ["Monday - Friday: 9am - 6pm", "Saturday - Sunday: Closed"],
      action: { label: "View Calendar", href: "#" }
    }
  ];

  const socialLinks = [
    { icon: <Linkedin className="w-6 h-6" />, name: "LinkedIn", url: "https://www.linkedin.com/company/aiguruhq" },
    { icon: <Facebook className="w-6 h-6" />, name: "Facebook", url: "https://www.facebook.com/aiguruhq" },
    { icon: <Instagram className="w-6 h-6" />, name: "Instagram", url: "https://www.instagram.com/aiguruhq" }
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Get in Touch</h1>
          <p className="text-xl max-w-3xl mx-auto opacity-90">
            Have questions about our services or want to discuss AI solutions for your organization? 
            We're here to help.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Information Section */}
          <div className="space-y-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
              <p className="text-lg text-gray-600 mb-8">
                Reach out to us through any of the following channels. Our team is ready to assist you.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {contactInfo.map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-blue-100 rounded-full mr-4">
                      {item.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                  </div>
                  <div className="space-y-1 mb-4">
                    {item.details.map((detail, idx) => (
                      <p key={idx} className="text-gray-600">{detail}</p>
                    ))}
                  </div>
                  <a 
                    href={item.action.href} 
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {item.action.label} →
                  </a>
                </div>
              ))}
            </div>

            {/* Social Media Section */}
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Connect With Us</h3>
              <div className="flex space-x-6">
                {socialLinks.map((social, index) => (
                  <a 
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow text-blue-600 hover:text-blue-800"
                    aria-label={`Follow us on ${social.name}`}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Google Map (Embed) */}
            <div className="w-full h-64 bg-gray-200 rounded-lg overflow-hidden shadow-md">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.0888739626075!2d-122.41941548428226!3d37.77492401975619!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDQ2JzI5LjciTiAxMjLCsDI1JzA3LjAiVw!5e0!3m2!1sen!2sus!4v1617740628540!5m2!1sen!2sus" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy"
                title="AI Guru Office Location"
              ></iframe>
            </div>
          </div>

          {/* Contact Form Section */}
          <div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              
              {formSubmitted ? (
                <Alert>
                  <AlertDescription className="text-green-700">
                    Thank you for your message! Our team will get back to you shortly.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <p className="text-lg text-gray-600 mb-8">
                    Fill out the form below and we'll respond as soon as possible.
                  </p>
                  
                  {!isFormLoaded && (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  )}
                  
                  <div id="hubspotForm" className="hubspot-form-container"></div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Frequently Asked Questions</h2>
          
          <div className="space-y-8">
            {[
              {
                question: "What types of AI solutions do you offer?",
                answer: "We offer a range of AI solutions including generative AI implementation, custom language model development, AI agents, and enterprise integration services. We also provide corporate training programs tailored to your organization's needs."
              },
              {
                question: "How quickly can you respond to inquiries?",
                answer: "We typically respond to all inquiries within 24 business hours. For urgent matters, please call our support line directly."
              },
              {
                question: "Do you offer virtual consultations?",
                answer: "Yes, we offer both in-person and virtual consultations depending on your preference and location. Our virtual training programs have received excellent feedback with a 4.83/5 customer satisfaction score."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
